import emailjs from "@emailjs/browser";
import { useState, useEffect } from "react";
// import TextareaAutosize from 'react-textarea-autosize';
import { name } from "./Logout.tsx";
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router-dom';
import { CircleCheck, Sparkles, Send, AlertTriangle } from 'lucide-react';  

// Chiave da usare in sessionStorage
const RELOAD_FLAG_KEY = 'goodbye_page_reloaded';

export function Goodbye() {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [isHovering, setIsHovering] = useState(false);

  // Reindirizza alla pagina iniziale
  useEffect(() => {
    const hasReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY) === 'true';

    if (hasReloaded) {
      sessionStorage.removeItem(RELOAD_FLAG_KEY); // Rimuovi il flag 
      navigate("/signup", { replace: true }); // Reindirizza subito
    } else {
      sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');
    }
    return () => {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    };
  }, [navigate]); 

  // Funzione per inviare la mail
  const sendFeedback = async () => {
    if (!feedback.trim()) return;
    setSending(true);
    setError("");
    setSent(false);
    try {
      await emailjs.send(
        "service_lxil62m",
        "template_ombx7eh",
        { name: name, message: feedback.trim() },
        process.env.REACT_APP_API_KEY_EMAILJS
      );
      setSent(true);
      setFeedback("");
    } catch (err) {
      const msg = (err as Error)?.message || "Si è verificato un errore durante l'invio, riprova.";
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  // Stile dinamico del pulsante
  const baseButtonStyle = {
    padding: '12px 32px',
    borderRadius: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s, transform 0.1s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    maxWidth: '300px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.06)'
  };

  const activeButtonStyle = {
    backgroundColor: isHovering ? '#4338CA' : '#4F46E5', // Hover o default (Indaco 700 / 600)
    color: 'white',
    transform: isHovering ? 'scale(1.01)' : 'scale(1)',
  };

  const disabledButtonStyle = {
    backgroundColor: '#A5B4FC', // Indaco 300
    color: 'white',
    cursor: 'not-allowed',
    boxShadow: 'none',
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '16px', 
      backgroundColor: '#F9FAFB' 
    }}>
      
      {/* Card principale */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '48px', 
        borderRadius: '16px', 
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', 
        width: '100%', 
        maxWidth: '512px', 
        textAlign: 'center' 
      }}>
        
        {/* Intestazione di Successo */}
        <div style={{ marginBottom: '32px' }}>
          <CircleCheck style={{ userSelect:'none', width: '48px', height: '48px', color: '#10B981', margin: '0 auto 16px auto' }} /> 
          <h1 style={{ fontSize: '2.25rem', fontWeight: '800', color: '#1F2937' }}>
            Account Eliminato con Successo
          </h1>
        </div>
        
        {/* Testo Descrittivo */}
        <p style={{ userSelect:'none', fontSize: '1.125rem', color: '#4B5563', marginBottom: '24px', lineHeight: '1.6' }}>
          Tutti i dati associati al tuo account sono stati rimossi in modo permanente dai nostri sistemi.
        </p>
        
        {sent ? (
          // Visualizza solo il messaggio di successo se 'sent' è true
          <div style={{ 
            marginTop: '40px', 
            marginBottom: '40px',
            padding: '20px', 
            backgroundColor: '#ECFDF5', // Sfondo verde chiaro
            borderRadius: '12px',
            border: '1px solid #A7F3D0'
          }}>
            <p style={{ userSelect:'none', color: '#047857', fontWeight: '700', fontSize: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <CircleCheck style={{ width: '24px', height: '24px' }} /> 
              Feedback inviato con successo!
            </p>
          </div>
        ) : (
          // Visualizza l'area di feedback, il bottone e l'errore se 'sent' è false
          <>
            <p style={{ userSelect:'none', fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '24px' }}>
              Per aiutarci a migliorare, ti andrebbe di lasciare un feedback?
            </p>
            
            {/* Area di Feedback */}
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="✍️ Scrivi qui il tuo feedback (perché hai eliminato l'account, suggerimenti, ecc.)"
              rows={4}
              style={{
                resize: 'vertical',
                width: '100%',
                maxHeight:'10rem',
                padding: '16px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                color: '#374151',
                fontFamily: 'inherit',
                outline: 'none',
                marginBottom: '16px',
                boxShadow: feedback ? `0 0 0 1px #6366F1` : '0 0 0 1px #D1D5DB'
              } as React.CSSProperties}
              onFocus={(e) => e.currentTarget.style.boxShadow = `0 0 0 2px #6366F1`}
              onBlur={(e) => e.currentTarget.style.boxShadow = `0 0 0 1px ${feedback ? '#6366F1' : '#D1D5DB'}`}
            />
    
            {/* Pulsante di Invio e Stato (Errore) */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
              <button
                onClick={sendFeedback}
                disabled={sending || !feedback.trim()}
                style={{
                  userSelect:'none', 
                  ...baseButtonStyle,
                  ...(sending || !feedback.trim() ? disabledButtonStyle : activeButtonStyle),
                }}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {sending ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={20} sx={{ mr: 1 , color:'#fff'}} />
                      <span>Invio...</span>
                    </Box>
                  </>
                ) : (
                  <>
                    <Send style={{ width: '20px', height: '20px' }} /> Invia Feedback
                  </>
                )}
              </button>
              
              {error && (
                <span style={{ color: '#EF4444', fontWeight: '500', fontSize: '0.875rem', textAlign: 'left', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <AlertTriangle style={{ width: '20px', height: '20px' }} /> {error}
                </span>
              )}
            </div>
          </>
        )}
        
        {/* Messaggio di Chiusura */}
        <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' }}>
          <p style={{ userSelect:'none', color: '#6B7280', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
            <Sparkles style={{ width: '16px', height: '16px', color: '#6366F1' }} />
            Grazie per aver utilizzato la nostra piattaforma .
          </p>
        </div>
      </div>
    </div>
  );
}


export default Goodbye;
